import type { AppState, RedirectLoginOptions } from '@auth0/auth0-react';

import type { CartUI } from '@libs/common-node';
import {
    getCountry,
    getPathnameFromPath,
    getSourceIdForCountry,
    isLastActiveExpired,
    NAVIGATION_URLS,
} from '@libs/core';

import type { PostLoginIntent } from '../components/Auth0/post-login-intent';
import { persistAuth0ReturnTo, resetAuth0CartMergeSignal } from './auth0CartMergeSync';
import { rememberAuth0FlowEntry } from './auth0FlowNavigation';
import {
    type Auth0LoginMethod,
    clearPersistedAuth0LoginMethod,
    persistAuth0AutoshipEnrollIntent,
    persistAuth0LoginMethod,
} from './auth0LoginAnalyticsSignal';
import { stripTrailingSlashes } from './getAuth0ReturnTo';
import { hasAutoshipInCart } from './hasFulfillmentMethodInCart';
import { cartHasVetDietItem, cartHasVetRxItem } from './vetRx';

export const AUTH0_SOURCE_ID_PARAM = 'ext-sourceId';
export const AUTH0_AUTOSHIP_PARAM = 'ext-autoship';
export const AUTH0_RX_FOOD_PARAM = 'ext-rx-food';
export const AUTH0_RX_MED_PARAM = 'ext-rx-med';
export const AUTH0_EXT_FLAG_VALUE = 'true';

const AUTH0_RESTRICTED_CHECKOUT_PATHNAMES = new Set([
    NAVIGATION_URLS.velocity.cart,
    NAVIGATION_URLS.velocity.checkout,
    NAVIGATION_URLS.velocity.checkoutLogin,
]);

/**
 * Cart, checkout, and checkout-login. Restricted `ext-*` flags are sent when a
 * redirect resumes one of these paths and the caller supplies the guest cart.
 */
export function isAuth0RestrictedCheckoutReturnTo(returnTo: string): boolean {
    const pathname = stripTrailingSlashes(getPathnameFromPath(returnTo)) || '/';
    return AUTH0_RESTRICTED_CHECKOUT_PATHNAMES.has(pathname);
}

export type Auth0AppState = AppState & {
    /**
     * Header signup on a browsing page. Unset when the caller passed a destination, and
     * unset on cart, checkout, checkout-login, and product pages so signup returns to that page.
     */
    returnToIsImplicit?: boolean;
    /** Transaction-bound marker used to validate in-store Autoship analytics on callback. */
    isInStoreAutoship?: boolean;
    /** A typed, consume-once action to resume after the callback reaches the destination app. */
    postLoginIntent?: PostLoginIntent;
};

type Auth0RedirectOptions = {
    returnTo: string;
    authorizationParams?: Record<string, string>;
    /**
     * Replace the current history entry instead of pushing a new one.
     * Required on auto-redirect pages (`/sign-in`, `/create-account`) so Safari
     * cannot bounce back to them instead of `/auth/callback`.
     */
    replaceHistory?: boolean;
    /**
     * Guest cart used to derive cart/checkout `ext-*` flags.
     * Omit for header, Treats, and other general sign-in — including clicks on cart or checkout.
     */
    cart?: CartUI | null;
    returnToIsImplicit?: boolean;
    /** Storefront path that launched Auth0, used when browser Back aborts the flow. */
    flowEntry?: string;
    /** True only when Auth0 was started by in-store Autoship enrollment. */
    isInStoreAutoship?: boolean;
    /** A typed, consume-once action to resume after successful authentication. */
    postLoginIntent?: PostLoginIntent;
};

type Auth0LoginRedirectOptions = Auth0RedirectOptions & {
    /**
     * Explicit `login_method` captured at authorize time.
     * Checkout must pass `'checkout'` so `/sign-in?returnTo=/checkout` cannot
     * overwrite the interstitial source with the viewport method.
     */
    loginMethod?: Auth0LoginMethod;
};

/**
 * Used by `loginWithRedirect({ openUrl })` so the authorize navigation does not
 * leave the interstitial page in history (`location.assign` is the SDK default).
 */
export function openAuth0AuthorizeUrl(url: string) {
    globalThis.location.replace(url);
}

export function getAuth0SourceIdAuthorizationParam(): Record<typeof AUTH0_SOURCE_ID_PARAM, string> {
    return {
        [AUTH0_SOURCE_ID_PARAM]: String(getSourceIdForCountry(getCountry())),
    };
}

/** Guest-cart category flags for Auth0 Actions. Only matching flags are sent. */
export function getAuth0CartCategoryAuthorizationParams(cart: CartUI | null = null): Record<string, string> {
    const params: Record<string, string> = {};

    if (hasAutoshipInCart(cart)) {
        params[AUTH0_AUTOSHIP_PARAM] = AUTH0_EXT_FLAG_VALUE;
    }

    if (cartHasVetDietItem(cart)) {
        params[AUTH0_RX_FOOD_PARAM] = AUTH0_EXT_FLAG_VALUE;
    }

    if (cartHasVetRxItem(cart)) {
        params[AUTH0_RX_MED_PARAM] = AUTH0_EXT_FLAG_VALUE;
    }

    return params;
}

/**
 * Builds options for authenticated Auth0 account-management flows. These
 * redirects share `/auth/callback` with login, so clear any abandoned login
 * method to prevent the callback from arming login analytics.
 */
export function getAuth0RedirectOptions({
    returnTo,
    authorizationParams,
    replaceHistory,
    cart,
    returnToIsImplicit,
    flowEntry,
    isInStoreAutoship = false,
    postLoginIntent,
}: Auth0RedirectOptions): RedirectLoginOptions {
    persistAuth0ReturnTo(returnTo);
    rememberAuth0FlowEntry(flowEntry);
    clearPersistedAuth0LoginMethod();
    persistAuth0AutoshipEnrollIntent(isInStoreAutoship);
    resetAuth0CartMergeSignal();

    // Flags follow the return path. Header and Treats omit `cart`, including on cart and checkout.
    const includeCartCategoryParams = isAuth0RestrictedCheckoutReturnTo(returnTo);

    return {
        appState: {
            returnTo,
            ...(returnToIsImplicit ? { returnToIsImplicit: true } : {}),
            ...(isInStoreAutoship ? { isInStoreAutoship: true } : {}),
            ...(postLoginIntent ? { postLoginIntent } : {}),
        } satisfies Auth0AppState,
        authorizationParams: {
            ...getAuth0SourceIdAuthorizationParam(),
            ...authorizationParams,
            ...(includeCartCategoryParams ? getAuth0CartCategoryAuthorizationParams(cart) : {}),
            // Inactivity wins over caller `prompt` so a later top-level authorize cannot
            // silently restore SSO after a public-page background logout.
            ...(isLastActiveExpired() ? { prompt: 'login' as const } : {}),
        },
        ...(replaceHistory ? { openUrl: openAuth0AuthorizeUrl } : {}),
    };
}

export function getAuth0LoginRedirectOptions({
    loginMethod,
    ...redirectOptions
}: Auth0LoginRedirectOptions): RedirectLoginOptions {
    const options = getAuth0RedirectOptions(redirectOptions);
    persistAuth0LoginMethod(loginMethod);
    return options;
}
